import React, { useMemo, useState } from 'react';
import {
  Container, Paper, Typography, Box, TextField, Button,
  CircularProgress, Alert, Grid, FormHelperText, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, Link, Stepper, Step, StepLabel, Chip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { useMutation, useQuery, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import PhoneNumberField from '../components/PhoneNumberField';
import SearchableSelect from '../components/SearchableSelect';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SubjectIcon from '@mui/icons-material/Subject';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DownloadIcon from '@mui/icons-material/Download';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';

// GraphQL queries
const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
    }
  }
`;

const GET_PUBLIC_HOSTS = gql`
  query GetPublicHosts($departmentId: ID!) {
    publicHosts(departmentId: $departmentId) {
      id
      firstName
      lastName
      email
      hostAvailabilities {
        id
        dayOfWeek
        startTime
        endTime
        isActive
      }
      function {
        id
        name
        parentFunction {
          id
          name
          parentFunction {
            id
            name
            parentFunction {
              id
              name
              parentFunction {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

const REQUEST_APPOINTMENT_VERIFICATION = gql`
  mutation RequestAppointmentVerification(
    $email: String!
    $firstName: String!
    $lastName: String!
    $phoneNumber: BigInt
    $departmentId: ID!
    $assignedToId: ID
    $subject: String!
    $description: String
    $scheduledAt: DateTime!
  ) {
    requestAppointmentVerification(
      email: $email
      firstName: $firstName
      lastName: $lastName
      phoneNumber: $phoneNumber
      departmentId: $departmentId
      assignedToId: $assignedToId
      subject: $subject
      description: $description
      scheduledAt: $scheduledAt
    ) {
      success
      message
    }
  }
`;

const CREATE_APPOINTMENT_WITH_VERIFICATION = gql`
  mutation CreateAppointmentWithVerification(
    $email: String!
    $verificationCode: String!
    $documents: [Upload]
  ) {
    createAppointmentWithVerification(
      email: $email
      verificationCode: $verificationCode
      documents: $documents
    ) {
      success
      message
      appointment {
        id
        email
        firstName
        lastName
        subject
        scheduledAt
        status
        visitRegistrationLink
        visitRegistrationQrBase64
        department {
          id
          name
        }
      }
    }
  }
`;

const normalizeDayOfWeek = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    if (/^\d+$/.test(value)) return Number(value);
    const match = value.match(/(\d+)$/);
    if (match) return Number(match[1]);
  }
  return null;
};

const getFunctionHierarchyDepth = (func) => {
  let depth = 0;
  let current = func?.parentFunction;
  while (current) {
    depth += 1;
    current = current.parentFunction;
  }
  return depth;
};

const steps = ['Informations', 'Vérification OTP', 'Confirmation'];

function AppointmentForm() {
  const APPOINTMENT_START_HOUR = 7;
  const APPOINTMENT_END_HOUR = 23;

  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    department_id: '',
    assigned_to_id: '',
    subject: '',
    description: '',
    scheduled_at: null,
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [documents, setDocuments] = useState([]);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [resendCodeMessage, setResendCodeMessage] = useState('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [createdAppointmentInfo, setCreatedAppointmentInfo] = useState(null);

  const { loading: departmentsLoading, data: departmentsData, error: departmentsError } = useQuery(GET_DEPARTMENTS);
  const { loading: hostsLoading, data: hostsData, error: hostsError } = useQuery(GET_PUBLIC_HOSTS, {
    variables: { departmentId: formData.department_id },
    skip: !formData.department_id,
    fetchPolicy: 'network-only'
  });

  const sortedHosts = useMemo(() => {
    const hosts = [...(hostsData?.publicHosts || [])];
    return hosts.sort((a, b) => {
      const depthA = getFunctionHierarchyDepth(a?.function);
      const depthB = getFunctionHierarchyDepth(b?.function);

      if (depthA !== depthB) {
        return depthA - depthB;
      }

      const functionNameCompare = String(a?.function?.name || '').localeCompare(String(b?.function?.name || ''));
      if (functionNameCompare !== 0) {
        return functionNameCompare;
      }

      return `${a?.firstName || ''} ${a?.lastName || ''}`.localeCompare(`${b?.firstName || ''} ${b?.lastName || ''}`);
    });
  }, [hostsData?.publicHosts]);

  const selectedHost = sortedHosts.find(
    (host) => String(host.id) === String(formData.assigned_to_id)
  );

  const getHostSlotsForDate = (date) => {
    if (!selectedHost?.hostAvailabilities?.length || !date) return [];
    const weekday = (date.getDay() + 6) % 7;
    return selectedHost.hostAvailabilities
      .filter((slot) => slot.isActive && normalizeDayOfWeek(slot.dayOfWeek) === weekday)
      .map((slot) => {
        const [startHour, startMinute] = String(slot.startTime).split(':').map(Number);
        const [endHour, endMinute] = String(slot.endTime).split(':').map(Number);
        return { startHour, startMinute, endHour, endMinute };
      });
  };

  const [requestVerification, { loading: requestingCode }] = useMutation(REQUEST_APPOINTMENT_VERIFICATION, {
    onCompleted: (data) => {
      if (data.requestAppointmentVerification.success) {
        setErrors({});
        setSuccessMessage('');
        setStep(2);
      } else {
        setErrors({ submit: data.requestAppointmentVerification.message });
      }
    },
    onError: (error) => {
      setErrors({ submit: error.message });
    },
  });

  const [createAppointment, { loading: creating }] = useMutation(CREATE_APPOINTMENT_WITH_VERIFICATION, {
    onCompleted: (data) => {
      if (data.createAppointmentWithVerification.success) {
        const createdAppointment = data.createAppointmentWithVerification.appointment;
        setFormData({
          email: '',
          first_name: '',
          last_name: '',
          phone_number: '',
          department_id: '',
          assigned_to_id: '',
          subject: '',
          description: '',
          scheduled_at: null,
        });
        setVerificationCode('');
        setDocuments([]);
        setErrors({});
        setSuccessMessage('');
        setCreatedAppointmentInfo(createdAppointment);
        setSuccessDialogOpen(true);
        setStep(1);
      } else {
        const errorMessage = data.createAppointmentWithVerification.message || 'Le code de vérification est incorrect. Veuillez réessayer.';
        setErrors({ submit: errorMessage });
        setVerificationCode('');
      }
    },
    onError: (error) => {
      const errorMessage = error.graphQLErrors?.[0]?.message || error.message || 'Une erreur est survenue lors de la vérification du code';
      setErrors({ submit: errorMessage });
      setVerificationCode('');
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'department_id' ? { assigned_to_id: '' } : {})
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, scheduled_at: date }));
    if (errors.scheduled_at) {
      setErrors(prev => ({ ...prev, scheduled_at: null }));
    }
  };

  const handleDocumentsChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 3) {
      setDocuments(selectedFiles.slice(0, 3));
      setErrors(prev => ({ ...prev, documents: 'Vous pouvez joindre au maximum 3 documents' }));
      return;
    }
    setDocuments(selectedFiles);
    if (errors.documents) {
      setErrors(prev => ({ ...prev, documents: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = "L'email est requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Format d'email invalide";
    if (!formData.first_name) newErrors.first_name = 'Le prénom est requis';
    if (!formData.last_name) newErrors.last_name = 'Le nom est requis';
    if (!formData.phone_number) newErrors.phone_number = 'Le téléphone est requis';
    if (!formData.department_id) newErrors.department_id = 'Le département est requis';
    if (!formData.assigned_to_id) newErrors.assigned_to_id = "L'hôte est requis";
    if (!formData.subject) newErrors.subject = 'Le sujet est requis';
    if (documents.length > 3) newErrors.documents = 'Vous pouvez joindre au maximum 3 documents';
    if (!formData.scheduled_at) newErrors.scheduled_at = "La date et l'heure sont requises";
    else if (formData.scheduled_at <= new Date()) newErrors.scheduled_at = 'La date doit être dans le futur';
    else {
      const hour = formData.scheduled_at.getHours();
      if (hour < APPOINTMENT_START_HOUR || hour >= APPOINTMENT_END_HOUR)
        newErrors.scheduled_at = "L'heure du rendez-vous doit être comprise entre 07:00 et 23:00";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toNumberOrNull = (value) => {
    if (!value) return null;
    const digits = String(value).replace(/\D/g, '');
    if (!digits) return null;
    return parseInt(digits, 10);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrors({});
    if (!validateForm()) return;
    requestVerification({
      variables: {
        email: formData.email,
        firstName: formData.first_name,
        lastName: formData.last_name,
        phoneNumber: toNumberOrNull(formData.phone_number),
        departmentId: formData.department_id,
        assignedToId: formData.assigned_to_id || null,
        subject: formData.subject,
        description: formData.description || null,
        scheduledAt: formData.scheduled_at.toISOString(),
      },
    });
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrors({});
    if (!verificationCode || verificationCode.length !== 6) {
      setErrors({ verification_code: 'Le code de vérification doit contenir 6 chiffres' });
      return;
    }
    createAppointment({
      variables: {
        email: formData.email,
        verificationCode: verificationCode,
        documents: documents.length ? documents : null,
      },
    });
  };

  if (departmentsLoading) {
    return (
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #e6f4f6 0%, #f0fafb 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box textAlign="center">
          <CircularProgress size={60} sx={{ color: '#006d78', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Chargement des données...</Typography>
        </Box>
      </Box>
    );
  }

  if (departmentsError) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" icon={<InfoIcon />}>
          Erreur lors du chargement des départements: {departmentsError.message}
        </Alert>
      </Container>
    );
  }

  const activeStep = step === 1 ? 0 : step === 2 ? 1 : 2;

  return (
    <Box sx={{ minHeight: '100vh', background: '#ffffff', pb: 6 }}>
      {/* Header Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #003d42 0%, #005a63 30%, #006d78 65%, #008a99 100%)',
          color: 'white',
          py: { xs: 3, md: 4 },
          px: 2,
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-40%', right: '-5%',
            width: 300, height: 300,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-60%', left: '10%',
            width: 200, height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 70, height: 70, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', mb: 2 }}>
            <CalendarMonthIcon sx={{ fontSize: 38 }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.6rem', md: '2rem' } }}>
            Prendre un rendez-vous
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, mb: 2 }}>
            Formulaire accessible sans connexion • Confirmation par email
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            {[{ icon: <SendIcon sx={{ fontSize: 14 }} />, label: 'Code OTP sécurisé' }, { icon: <CalendarMonthIcon sx={{ fontSize: 14 }} />, label: '07h00 - 23h00' }, { icon: <QrCode2Icon sx={{ fontSize: 14 }} />, label: 'QR code de suivi' }].map((item, i) => (
              <Chip
                key={i}
                icon={item.icon}
                label={item.label}
                
                sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', backdropFilter: 'blur(5px)', border: '1px solid rgba(255,255,255,0.3)', '& .MuiChip-icon': { color: 'white' } }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md">
        {/* Stepper */}
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3, background: 'white', border: '1px solid rgba(210,20,51,0.1)' }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      '&.Mui-active': { color: '#006d78' },
                      '&.Mui-completed': { color: '#006d78' },
                    }
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: activeStep === index ? 700 : 400 }}>
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Main Form Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: 4,
            border: '1px solid rgba(210,20,51,0.12)',
            background: 'white',
            boxShadow: '0 20px 60px rgba(210,20,51,0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 25px 70px rgba(210,20,51,0.15)',
            },
          }}
        >
          {/* Info Alert */}
          {step === 1 && (
            <Alert
              severity="info"
              icon={<InfoIcon />}
              sx={{
                mb: 3,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #e6f4f6, #d0eef1)',
                border: '1px solid rgba(0,109,120,0.2)',
                '& .MuiAlert-icon': { color: '#005a63' },
              }}
            >
              Après validation du code OTP, votre rendez-vous est créé et un lien de suivi vous est envoyé par email.
            </Alert>
          )}

          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
              {errors.submit}
            </Alert>
          )}

          {/* STEP 1 - Form */}
          {step === 1 && (
            <form onSubmit={handleSubmit}>
              {/* Section: Identité */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{ width: 4, height: 20, background: 'linear-gradient(180deg, #006d78, #005a63)', borderRadius: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                    Identité du visiteur
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Prénom"
                      name="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, '').slice(0, 100);
                        handleChange({ target: { name: 'first_name', value } });
                      }}
                      error={!!errors.first_name}
                      helperText={errors.first_name || ''}
                      required
                      
                      InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: errors.first_name ? 'error.main' : '#006d78', fontSize: 20 }} /> }}
                      inputProps={{ maxLength: 100, pattern: "[a-zA-ZÀ-ÿ\\s'-]*" }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom"
                      name="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, '').slice(0, 100);
                        handleChange({ target: { name: 'last_name', value } });
                      }}
                      error={!!errors.last_name}
                      helperText={errors.last_name || ''}
                      required
                      
                      InputProps={{ startAdornment: <BadgeIcon sx={{ mr: 1, color: errors.last_name ? 'error.main' : '#006d78', fontSize: 20 }} /> }}
                      inputProps={{ maxLength: 100, pattern: "[a-zA-ZÀ-ÿ\\s'-]*" }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email || ''}
                      required
                      
                      InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: errors.email ? 'error.main' : '#006d78', fontSize: 20 }} /> }}
                      inputProps={{ maxLength: 254, autoComplete: 'email' }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PhoneNumberField
                      label="Téléphone"
                      name="phone_number"
                      id="phone_number"
                      value={formData.phone_number}
                      required
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, phone_number: value || '' }));
                        if (errors.phone_number) setErrors(prev => ({ ...prev, phone_number: null }));
                      }}
                      error={!!errors.phone_number}
                      helperText={errors.phone_number || ''}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2.5, borderColor: 'rgba(210,20,51,0.1)' }} />

              {/* Section: Rendez-vous */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{ width: 4, height: 20, background: 'linear-gradient(180deg, #007481, #005a63)', borderRadius: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                    Détails du rendez-vous
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SearchableSelect
                      label="Département"
                      value={formData.department_id}
                      onChange={(value) => handleChange({ target: { name: 'department_id', value } })}
                      options={departmentsData?.departments || []}
                      getOptionLabel={(option) => option?.name || ''}
                      getOptionValue={(option) => option?.id || ''}
                      required
                      error={!!errors.department_id}
                      helperText={errors.department_id || ''}
                      
                      noOptionsText="Aucun département trouvé"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SearchableSelect
                      label="Hôte"
                      value={formData.assigned_to_id}
                      onChange={(value) => handleChange({ target: { name: 'assigned_to_id', value } })}
                      options={sortedHosts}
                      getOptionLabel={(option) => {
                        const functionName = option?.function?.name || '';
                        return functionName || '';
                      }}
                      getOptionValue={(option) => option?.id || ''}
                      required
                      disabled={!formData.department_id || hostsLoading}
                      loading={hostsLoading}
                      error={!!errors.assigned_to_id}
                      helperText={
                        errors.assigned_to_id || (!formData.department_id ? '' : hostsLoading ? 'Chargement des hôtes...' : hostsError ? 'Erreur de chargement des hôtes' : (hostsData?.publicHosts?.length || 0) === 0 ? 'Aucun hôte disponible pour ce département' : '')
                      }
                      
                      noOptionsText={hostsLoading ? 'Chargement des hôtes...' : 'Aucun hôte trouvé'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Sujet"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                      error={!!errors.subject}
                      helperText={errors.subject || ''}
                      required
                      
                      InputProps={{ startAdornment: <SubjectIcon sx={{ mr: 1, color: errors.subject ? 'error.main' : '#ffc71f', fontSize: 20 }} /> }}
                      inputProps={{ maxLength: 200 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description (optionnel)"
                      name="description"
                      type="text"
                      value={formData.description}
                      onChange={handleChange}
                      multiline
                      rows={3}
                      
                      InputProps={{ startAdornment: <DescriptionIcon sx={{ mr: 1, mt: 0.5, color: '#ffc71f', fontSize: 20, alignSelf: 'flex-start' }} /> }}
                      inputProps={{ maxLength: 1000 }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2.5, borderColor: 'rgba(210,20,51,0.1)' }} />

              {/* Section: Documents & Date */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{ width: 4, height: 20, background: 'linear-gradient(180deg, #ffc71f, #e6b000)', borderRadius: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                    Documents & Planification
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: `2px dashed ${errors.documents ? '#d32f2f' : 'rgba(210,20,51,0.3)'}`,
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        background: 'rgba(210,20,51,0.02)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#006d78',
                          background: 'rgba(210,20,51,0.05)',
                        },
                      }}
                    >
                      <Button
                        variant="outlined"
                        component="label"
                        
                        startIcon={<AttachFileIcon />}
                        sx={{ borderRadius: 2, mb: 1 }}
                      >
                        Joindre des documents (max 3)
                        <input type="file" hidden multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleDocumentsChange} />
                      </Button>
                      <FormHelperText error={!!errors.documents} sx={{ textAlign: 'center', mt: 0 }}>
                        {errors.documents || (documents.length
                          ? `${documents.length} fichier(s) : ${documents.map(f => f.name).join(', ')}`
                          : 'Formats autorisés: PDF, DOC, DOCX, JPG, PNG')}
                      </FormHelperText>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                      <DateTimePicker
                        label="Date et heure du rendez-vous *"
                        value={formData.scheduled_at}
                        onChange={handleDateChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            error={!!errors.scheduled_at}
                            helperText={errors.scheduled_at}
                            required
                            
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        )}
                        minDateTime={new Date()}
                        shouldDisableDate={(date) => {
                          if (!selectedHost) return false;
                          return getHostSlotsForDate(date).length === 0;
                        }}
                        shouldDisableTime={(value, view) => {
                          const activeDate = formData.scheduled_at || new Date();
                          const slots = getHostSlotsForDate(activeDate);
                          if (!slots.length) return true;
                          if (view === 'hours') {
                            const hour = typeof value === 'number' ? value : (value?.getHours?.() ?? value?.hour?.() ?? 0);
                            if (hour < APPOINTMENT_START_HOUR || hour >= APPOINTMENT_END_HOUR) return true;
                            return !slots.some((slot) => {
                              if (hour > slot.startHour && hour < slot.endHour) return true;
                              if (hour === slot.startHour && slot.startMinute < 60) return true;
                              if (hour === slot.endHour && slot.endMinute > 0) return true;
                              return false;
                            });
                          }
                          if (view === 'minutes') {
                            const minute = typeof value === 'number' ? value : (value?.getMinutes?.() ?? value?.minute?.() ?? 0);
                            const hour = formData.scheduled_at?.getHours?.() ?? 0;
                            return !slots.some((slot) => {
                              const total = hour * 60 + minute;
                              return total >= slot.startHour * 60 + slot.startMinute && total < slot.endHour * 60 + slot.endMinute;
                            });
                          }
                          return false;
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 1, borderTop: '1px solid rgba(210,20,51,0.1)' }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate('/')}
                  disabled={requestingCode}
                  startIcon={<CancelIcon />}
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={requestingCode}
                  startIcon={requestingCode ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    background: 'linear-gradient(135deg, #006d78 0%, #005a63 100%)',
                    fontWeight: 700,
                    boxShadow: '0 4px 15px rgba(210,20,51,0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #005a63 0%, #003d42 100%)',
                      boxShadow: '0 6px 20px rgba(210,20,51,0.5)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {requestingCode ? 'Envoi en cours...' : 'Envoyer le code OTP'}
                </Button>
              </Box>
            </form>
          )}

          {/* STEP 2 - OTP Verification */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode}>
              {resendCodeMessage && (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                  {resendCodeMessage}
                </Alert>
              )}

              <Box
                sx={{
                  textAlign: 'center',
                  mb: 4,
                  p: 3,
                  background: 'linear-gradient(135deg, #e6f4f6, #d0eef1)',
                  borderRadius: 3,
                  border: '1px solid rgba(0,109,120,0.2)',
                }}
              >
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #006d78, #005a63)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2, boxShadow: '0 8px 25px rgba(0,109,120,0.4)' }}>
                  <VpnKeyIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#006d78', mb: 1 }}>
                  Vérification de l'email
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Un code à 6 chiffres a été envoyé à{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: '#006d78' }}>
                    {formData.email}
                  </Box>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Vérifiez vos spams si vous ne le trouvez pas
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <TextField
                      label="Code OTP à 6 chiffres"
                      name="verification_code"
                      type="text"
                      value={verificationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setVerificationCode(value);
                        if (errors.verification_code) setErrors(prev => ({ ...prev, verification_code: null }));
                      }}
                      error={!!errors.verification_code}
                      helperText={errors.verification_code || 'Saisissez le code reçu par email'}
                      required
                      inputProps={{ maxLength: 6, minLength: 6, inputMode: 'numeric' }}
                      placeholder="000000"
                      autoFocus
                      sx={{
                        width: 280,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: '1.8rem',
                          fontWeight: 700,
                          letterSpacing: '0.5em',
                          textAlign: 'center',
                        },
                        '& input': { textAlign: 'center' },
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
                    <strong>Code non reçu ?</strong> Attendez quelques minutes ou cliquez sur "Renvoyer".
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap', pt: 1, borderTop: '1px solid rgba(210,20,51,0.1)' }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => { setStep(1); setVerificationCode(''); setErrors({}); setSuccessMessage(''); setResendCodeMessage(''); }}
                        disabled={creating || requestingCode}
                        startIcon={<ArrowBackIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        Retour
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => {
                          setErrors({});
                          setSuccessMessage('');
                          setResendCodeMessage('');
                          setVerificationCode('');
                          requestVerification({
                            variables: {
                              email: formData.email,
                              firstName: formData.first_name,
                              lastName: formData.last_name,
                              phoneNumber: toNumberOrNull(formData.phone_number),
                              departmentId: formData.department_id,
                              assignedToId: formData.assigned_to_id || null,
                              subject: formData.subject,
                              description: formData.description || null,
                              scheduledAt: formData.scheduled_at.toISOString(),
                            },
                            onCompleted: (data) => {
                              if (data.requestAppointmentVerification.success) {
                                setStep(2);
                                setResendCodeMessage('✅ Nouveau code envoyé à ' + formData.email);
                                setTimeout(() => setResendCodeMessage(''), 5000);
                              } else {
                                setErrors({ submit: data.requestAppointmentVerification.message });
                              }
                            },
                            onError: (error) => { setErrors({ submit: error.message }); setStep(2); },
                          });
                        }}
                        disabled={requestingCode || creating}
                        startIcon={requestingCode ? <CircularProgress size={16} /> : <RefreshIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        {requestingCode ? 'Envoi...' : 'Renvoyer'}
                      </Button>
                    </Box>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={creating || verificationCode.length !== 6 || requestingCode}
                      startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        background: 'linear-gradient(135deg, #007481 0%, #005a63 100%)',
                        fontWeight: 700,
                        boxShadow: '0 4px 15px rgba(56,142,60,0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #005a63 0%, #004a52 100%)',
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': { background: '#e0e0e0' },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {creating ? 'Vérification...' : 'Confirmer le rendez-vous'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}
        </Paper>
      </Container>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #007481, #005a63)',
            color: 'white',
            py: 3,
            textAlign: 'center',
          }}
        >
          <EventAvailableIcon sx={{ fontSize: 48, display: 'block', mx: 'auto', mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Rendez-vous confirmé !
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: 2 }}>
            Votre rendez-vous a été créé avec succès. Un email de confirmation avec le lien de suivi vous a été envoyé.
          </Alert>

          {createdAppointmentInfo?.visitRegistrationLink && (
            <Box sx={{ mb: 2, p: 2, background: '#f3f8ff', borderRadius: 2, border: '1px solid rgba(0,109,120,0.2)' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ fontSize: 16 }} /> Lien de suivi
              </Typography>
              <Link href={createdAppointmentInfo.visitRegistrationLink} target="_blank" rel="noreferrer" sx={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                Cliquer ici pour suivre votre rendez-vous
              </Link>
            </Box>
          )}

          {createdAppointmentInfo?.visitRegistrationQrBase64 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <QrCode2Icon sx={{ fontSize: 18 }} /> QR Code de suivi
              </Typography>
              <Box
                component="img"
                src={`data:image/png;base64,${createdAppointmentInfo.visitRegistrationQrBase64}`}
                alt="QR code de suivi"
                sx={{ width: 200, height: 200, maxWidth: '100%', border: '2px solid rgba(210,20,51,0.2)', borderRadius: 2, p: 1 }}
              />
              <Button
                component="a"
                href={`data:image/png;base64,${createdAppointmentInfo.visitRegistrationQrBase64}`}
                download={`qr-rendez-vous-${createdAppointmentInfo.id || 'suivi'}.png`}
                variant="outlined"
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: 2 }}
              >
                Télécharger le QR code
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button onClick={() => setSuccessDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Fermer
          </Button>
          <Button
            onClick={() => { setSuccessDialogOpen(false); navigate('/'); }}
            variant="contained"
            startIcon={<HomeIcon />}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #006d78, #005a63)', fontWeight: 700 }}
          >
            Retour à l'accueil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AppointmentForm;
